import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FaShieldAlt, FaBolt, FaServer, FaLock, FaCogs, FaHeadset, FaCheck, FaCheckCircle, FaCode, FaDesktop, FaMobileAlt, FaWhatsapp } from 'react-icons/fa';
import styles from './Elohim.module.css';
import PageSEO from '../../components/PageSEO/PageSEO';

const ELOHIM_CENTRAL_URL = import.meta.env.VITE_ELOHIM_CENTRAL_URL;

const Elohim = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || 'pt';

  const features = [
    { icon: <FaShieldAlt />, title: t('elohim.f1'), description: t('elohim.f1Desc') },
    { icon: <FaCogs />,      title: t('elohim.f2'), description: t('elohim.f2Desc') },
    { icon: <FaBolt />,      title: t('elohim.f3'), description: t('elohim.f3Desc') },
    { icon: <FaServer />,    title: t('elohim.f4'), description: t('elohim.f4Desc') },
    { icon: <FaLock />,      title: t('elohim.f5'), description: t('elohim.f5Desc') },
    { icon: <FaHeadset />,   title: t('elohim.f6'), description: t('elohim.f6Desc') },
  ];

  const planFeatures = {
    micro:        ['Até 200 pessoas cadastradas',    'Até 3 faces por pessoa',  '1 ativação',  t('elohim.f1'), 'Dashboard Elohim Central', 'Atualizações do sistema',    'Suporte básico'],
    starter:      ['Até 1.000 pessoas cadastradas',  'Até 5 faces por pessoa',  '1 ativação',  t('elohim.f1'), 'Dashboard Elohim Central', 'Atualizações automáticas',   'Suporte básico'],
    professional: ['Até 10.000 pessoas cadastradas', 'Até 10 faces por pessoa', '3 ativações', t('elohim.f1'), 'API REST para integração',  'Dashboard completo',         'Suporte prioritário'],
    enterprise:   ['Até 100.000 pessoas cadastradas','Até 20 faces por pessoa', '10 ativações',t('elohim.f1'), 'API completa + Webhooks',   'Dashboard multiunidade',     'Relatórios avançados', 'Suporte dedicado'],
  };

  const planDescriptions = {
    micro:        'Ideal para pequenos escritórios, clínicas, igrejas e operações pequenas.',
    starter:      'Ideal para academias pequenas, condomínios e pequenas empresas.',
    professional: 'Ideal para empresas, escolas e redes de academias.',
    enterprise:   'Ideal para operações de grande escala e ambientes corporativos.',
  };

  const planButtons = {
    micro:        t('elohim.comecarAgora'),
    starter:      t('elohim.queroPlano'),
    professional: t('elohim.escolherProf'),
    enterprise:   t('elohim.falarComercial'),
  };

  const useCases = [
    'Controle de acesso', 'Registro de presença', 'Academias', 'Igrejas',
    'Empresas', 'Escolas', 'Eventos', 'Condomínios',
  ];

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState(false);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [checkoutStep, setCheckoutStep] = useState(1);

  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formDocument, setFormDocument] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const [pixData, setPixData] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [licenseKey, setLicenseKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const pollingRef = useRef(null);
  const pricingRef = useRef(null);

  useEffect(() => {
    const trackVisit = async () => {
      try {
        let ipV4 = null;
        try {
          const ipRes = await fetch('https://api.ipify.org?format=json');
          const ipData = await ipRes.json();
          ipV4 = ipData.ip;
        } catch { /* proceed without IPv4 */ }

        const params = new URLSearchParams(window.location.search);
        await fetch(`${ELOHIM_CENTRAL_URL}/api/v1/tracking/visit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: '/Elohim',
            referer: document.referrer || null,
            ip_v4: ipV4,
            utm_source: params.get('utm_source') || null,
            utm_medium: params.get('utm_medium') || null,
            utm_campaign: params.get('utm_campaign') || null,
          }),
        });
      } catch { /* tracking is non-critical */ }
    };
    trackVisit();
  }, []);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await fetch(`${ELOHIM_CENTRAL_URL}/api/v1/checkout/plans`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const order = ['micro', 'starter', 'professional', 'enterprise'];
        data.sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug));
        setPlans(data);
      } catch {
        setPlansError(true);
      } finally {
        setPlansLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const startPolling = useCallback((oid) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${ELOHIM_CENTRAL_URL}/api/v1/checkout/orders/${oid}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.status === 'approved') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          setLicenseKey(data.license_key);
          setCheckoutStep(3);
        } else if (data.status === 'rejected' || data.status === 'cancelled') {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      } catch { /* ignore */ }
    }, 5000);
  }, []);

  useEffect(() => {
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth' });

  const openCheckout = (plan) => {
    setSelectedPlan(plan);
    setCheckoutStep(1);
    setFormName(''); setFormEmail(''); setFormPhone(''); setFormDocument('');
    setFormErrors({}); setPixData(null); setOrderId(null);
    setLicenseKey(null); setCopied(false);
    setCheckoutOpen(true);
  };

  const closeCheckout = () => {
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
    setCheckoutOpen(false);
  };

  const validateForm = () => {
    const errors = {};
    if (!formName.trim() || formName.trim().length < 3) errors.name = 'Nome completo é obrigatório';
    if (!formEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formEmail)) errors.email = 'Email inválido';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setFormSubmitting(true);
    try {
      const body = {
        plan_id: selectedPlan.id,
        customer_name: formName.trim(),
        customer_email: formEmail.trim(),
      };
      if (formPhone.trim()) body.customer_phone = formPhone.trim();
      if (formDocument.trim()) body.customer_document = formDocument.trim();

      const res = await fetch(`${ELOHIM_CENTRAL_URL}/api/v1/checkout/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao gerar pagamento');
      }

      const data = await res.json();
      setPixData(data);
      setOrderId(data.order_id);
      setCheckoutStep(2);
      startPolling(data.order_id);
    } catch (err) {
      setFormErrors({ submit: err.message || 'Falha ao processar. Tente novamente.' });
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleCopyPix = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const formatPrice = (price) =>
    Number(price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isPopular = (plan) => plan.slug === 'professional';

  const handlePlanAction = (plan) => {
    if (plan.slug === 'enterprise') window.location.href = '/Contato';
    else openCheckout(plan);
  };

  const getPlanPeriod = (plan) => {
    if (plan.license_duration_days === 30) return '/mês';
    if (plan.license_duration_days >= 365) return '/ano';
    return `/${plan.license_duration_days} dias`;
  };

  return (
    <div className={styles.container}>
      <PageSEO
        title="Elohim — Reconhecimento Facial Inteligente"
        description="API de reconhecimento facial on-premise de alta precisão. Instalação local, sem dependência de nuvem, integração REST com qualquer linguagem. Planos a partir de R$ 97/mês."
        path="/Elohim"
        lang={lang}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          name: 'Elohim',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Windows, Linux',
          description: 'API de reconhecimento facial on-premise com alta precisão e instalação local.',
          offers: { '@type': 'AggregateOffer', priceCurrency: 'BRL', lowPrice: '97', highPrice: '997' },
          url: 'https://steinsoft.com.br/Elohim',
        }}
      />
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.title}>
            <span className={styles.productName}>Elohim</span>
            <span className={styles.tagline}>{t('elohim.titulo')}</span>
          </h1>
          <p className={styles.subtitle}>{t('elohim.subtitulo')}</p>
          <div className={styles.heroButtons}>
            <button className={styles.primaryButton} onClick={scrollToPricing}>
              {t('elohim.btnPlanos')}
            </button>
          </div>
        </div>
        <div className={styles.heroVisual}>
          <div className={styles.heroIcon}><FaShieldAlt /></div>
        </div>
      </section>

      {/* Features */}
      <section className={styles.features}>
        <h2 className={styles.sectionTitle}>{t('elohim.f1').replace('em Tempo Real','').trim()}</h2>
        <div className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <div key={index} className={styles.featureCard}>
              <div className={styles.featureIcon}>{feature.icon}</div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className={styles.pricing} ref={pricingRef}>
        <div className={styles.pricingHeader}>
          <h2 className={styles.sectionTitle}>{t('elohim.planosTitulo')}</h2>
          <p className={styles.pricingSubtitle}>{t('elohim.planosSubtitulo')}</p>
        </div>
        {plansLoading ? (
          <p className={styles.pricingLoading}>{t('elohim.carregandoPlanos')}</p>
        ) : plansError ? (
          <p className={styles.pricingError}>{t('elohim.erroPlanos')}</p>
        ) : (
          <>
            <div className={styles.pricingGrid}>
              {plans.map((plan) => (
                <div key={plan.id} className={`${styles.planCard} ${isPopular(plan) ? styles.planCardPopular : ''}`}>
                  {isPopular(plan) && <div className={styles.popularBadge}>{t('elohim.maisPopular')}</div>}
                  <h3 className={styles.planName}>{plan.name}</h3>
                  <div className={styles.planPrice}>
                    <span className={styles.priceCurrency}>R$ </span>
                    <span className={styles.priceValue}>{formatPrice(plan.price)}</span>
                    <span className={styles.pricePeriod}> {getPlanPeriod(plan)}</span>
                  </div>
                  {planDescriptions[plan.slug] && (
                    <p className={styles.planDescription}>{planDescriptions[plan.slug]}</p>
                  )}
                  <ul className={styles.planFeatures}>
                    {(planFeatures[plan.slug] || []).map((feature, idx) => (
                      <li key={idx}><FaCheck className={styles.checkIcon} /> {feature}</li>
                    ))}
                  </ul>
                  <button
                    className={`${styles.planButton} ${isPopular(plan) ? styles.planButtonPopular : ''}`}
                    onClick={() => handlePlanAction(plan)}
                  >
                    {planButtons[plan.slug] || t('elohim.comecarAgora')}
                  </button>
                </div>
              ))}
            </div>
            <p className={styles.pricingMicrocopy}>{t('elohim.planosSubtitulo')}</p>
          </>
        )}
      </section>

      {/* Use Cases */}
      <section className={styles.useCases}>
        <h2 className={styles.sectionTitle}>{t('elohim.ondeUsado')}</h2>
        <div className={styles.useCasesGrid}>
          {useCases.map((item, index) => (
            <div key={index} className={styles.useCaseItem}>
              <FaCheck className={styles.useCaseIcon} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Demos & Integration */}
      <section className={styles.demos}>
        <h2 className={styles.sectionTitle}>{t('elohim.demosTitulo')}</h2>
        <p className={styles.demosSubtitle}>{t('elohim.demosSubtitulo')}</p>
        <div className={styles.demosGrid}>
          <div className={styles.demoCard}>
            <div className={styles.demoIcon}><FaDesktop /></div>
            <h3>{t('elohim.demo1')}</h3>
            <p>{t('elohim.demo1Desc')}</p>
            <span className={styles.demoTag}>{t('elohim.demo1Tag')}</span>
          </div>
          <div className={styles.demoCard}>
            <div className={styles.demoIcon}><FaMobileAlt /></div>
            <h3>{t('elohim.demo2')}</h3>
            <p>{t('elohim.demo2Desc')}</p>
            <span className={styles.demoTag}>{t('elohim.demo2Tag')}</span>
          </div>
          <div className={styles.demoCard}>
            <div className={styles.demoIcon}><FaCode /></div>
            <h3>{t('elohim.demo3')}</h3>
            <p>{t('elohim.demo3Desc')}</p>
            <span className={styles.demoTag}>{t('elohim.demo3Tag')}</span>
          </div>
        </div>
      </section>

      {/* Checkout Modal */}
      {checkoutOpen && (
        <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) closeCheckout(); }}>
          <div className={styles.modal}>
            <div className={styles.modalHeader}>
              <h2>
                {checkoutStep === 1 && t('elohim.compraNome').replace(' *','').replace('Nome Completo','Dados para Compra')}
                {checkoutStep === 2 && 'Pagamento PIX'}
                {checkoutStep === 3 && t('elohim.pagamentoConfirmado')}
              </h2>
              <button className={styles.modalClose} onClick={closeCheckout}>&times;</button>
            </div>
            <div className={styles.modalBody}>
              {checkoutStep === 1 && (
                <form onSubmit={handleFormSubmit}>
                  <div className={styles.formGroup}>
                    <label>{t('elohim.compraNome')}</label>
                    <input className={styles.formInput} type="text" value={formName}
                      onChange={(e) => setFormName(e.target.value)} placeholder="Seu nome completo" />
                    {formErrors.name && <p className={styles.formError}>{formErrors.name}</p>}
                  </div>
                  <div className={styles.formGroup}>
                    <label>{t('elohim.compraEmail')}</label>
                    <input className={styles.formInput} type="email" value={formEmail}
                      onChange={(e) => setFormEmail(e.target.value)} placeholder="seu@email.com" />
                    {formErrors.email && <p className={styles.formError}>{formErrors.email}</p>}
                  </div>
                  <div className={styles.formGroup}>
                    <label>{t('elohim.compraTelefone')}</label>
                    <input className={styles.formInput} type="tel" value={formPhone}
                      onChange={(e) => setFormPhone(e.target.value)} placeholder="(11) 99999-9999" />
                  </div>
                  <div className={styles.formGroup}>
                    <label>{t('elohim.compraCpf')}</label>
                    <input className={styles.formInput} type="text" value={formDocument}
                      onChange={(e) => setFormDocument(e.target.value)} placeholder="000.000.000-00" />
                  </div>
                  {formErrors.submit && <p className={styles.formError}>{formErrors.submit}</p>}
                  <button type="submit" className={styles.submitButton} disabled={formSubmitting}>
                    {formSubmitting ? t('elohim.gerandoPix') : `Pagar R$ ${formatPrice(selectedPlan.price)}`}
                  </button>
                </form>
              )}

              {checkoutStep === 2 && pixData && (
                <div className={styles.pixContainer}>
                  <p className={styles.pixAmount}>R$ {formatPrice(pixData.amount)}</p>
                  <p style={{ color: '#b0b0b0', marginBottom: 15 }}>Plano {pixData.plan_name}</p>
                  {pixData.qr_code_base64 && (
                    <div className={styles.pixQr}>
                      <img src={`data:image/png;base64,${pixData.qr_code_base64}`} alt="QR Code PIX" />
                    </div>
                  )}
                  {pixData.qr_code && (
                    <div className={styles.pixCode}>
                      <label>{t('elohim.pixCopiaECola')}</label>
                      <div className={styles.pixCodeBox}>
                        <input type="text" value={pixData.qr_code} readOnly />
                        <button className={styles.copyButton} onClick={handleCopyPix}>
                          {copied ? t('elohim.copiado') : t('elohim.copiar')}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className={styles.pixStatus}>
                    <div className={styles.spinner}></div>
                    {t('elohim.aguardandoPagamento')}
                  </div>
                </div>
              )}

              {checkoutStep === 3 && (
                <div className={styles.successContainer}>
                  <div className={styles.successIcon}><FaCheckCircle /></div>
                  <h3>{t('elohim.pagamentoConfirmado')}</h3>
                  <p>{t('elohim.licencaEnviada')} <strong>{formEmail}</strong></p>
                  {licenseKey && (
                    <div className={styles.licenseKeyBox}>
                      <label>{t('elohim.chaveDeL')}</label>
                      <div className={styles.licenseKey}>{licenseKey}</div>
                    </div>
                  )}
                  <button className={styles.doneButton} onClick={closeCheckout}>
                    {t('elohim.fechar')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <a
        href="https://wa.me/5548988393447?text=Ol%C3%A1%2C%20tenho%20interesse%20no%20Elohim!"
        target="_blank" rel="noopener noreferrer"
        className={styles.whatsappFloat} aria-label={t('elohim.falarWhatsApp')}
      >
        <FaWhatsapp />
      </a>
    </div>
  );
};

export default Elohim;
